import { Box, Dialog, DialogContent, DialogTitle, Divider, Icon, IconButton, TextField } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";

import Keycap from "~/components/keyboard/Keycap";
import { BodyTextSmall, CaptionText, SubtitleText } from "~/components/Typographies";
import useKeybindingOverrides from "~/hooks/useKeybindingOverrides";
import keyboardActions, { effectiveBindingsForAction } from "~/keyboard/actionCatalog";
import { detectIsMac } from "~/keyboard/bindings";
import KeyboardShortcutsContext from "~/keyboard/KeyboardShortcutsContext";
import buildHelpSections, { filterHelpSections } from "~/keyboard/keyboardHelpModel";
import shortcutsOverlayControl from "~/keyboard/shortcutsOverlayControl";
import useShortcut from "~/keyboard/useShortcut";

const KeyboardShortcutsOverlay = () => {
	const { getRegisteredActionIds } = useContext(KeyboardShortcutsContext);
	const { overrides } = useKeybindingOverrides();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const isMac = useMemo(() => detectIsMac(), []);

	useShortcut("help.showShortcuts", () => {
		setSearch("");
		setOpen(true);
		return true;
	});

	useEffect(
		() =>
			shortcutsOverlayControl.subscribe(() => {
				setSearch("");
				setOpen(true);
			}),
		[]
	);

	useShortcut(
		"help.close",
		() => {
			setOpen(false);
			return true;
		},
		{ enabled: open }
	);

	const sections = useMemo(() => {
		if (!open) return [];
		const built = buildHelpSections({
			actions: keyboardActions,
			registeredActionIds: getRegisteredActionIds(),
			resolveBindings: (action) => effectiveBindingsForAction(action, overrides)
		});
		return filterHelpSections(built, search);
	}, [open, search, getRegisteredActionIds, overrides]);

	return (
		<Dialog
			disableEscapeKeyDown
			fullWidth
			maxWidth="sm"
			onClose={() => setOpen(false)}
			open={open}
			PaperProps={{ sx: { maxHeight: "80vh" } }}
		>
			<DialogTitle sx={{ alignItems: "center", display: "flex", gap: 1, pb: 1 }}>
				<Icon sx={{ color: "primary.main" }}>keyboard</Icon>
				<SubtitleText bold sx={{ flex: 1 }}>
					Keyboard shortcuts
				</SubtitleText>
				<IconButton aria-label="close shortcuts" onClick={() => setOpen(false)} size="small">
					<Icon sx={{ fontSize: "1.15rem" }}>close</Icon>
				</IconButton>
			</DialogTitle>
			<Box sx={{ px: 3, pb: 1 }}>
				<TextField
					autoFocus
					fullWidth
					onChange={(event) => setSearch(event.target.value)}
					placeholder="Search shortcuts"
					size="small"
					value={search}
				/>
			</Box>
			<DialogContent dividers>
				{sections.length === 0 ? (
					<CaptionText sx={{ color: "text.secondary" }}>No shortcuts match your search.</CaptionText>
				) : (
					sections.map((section) => (
						<Box key={section.id} sx={{ mb: 2.5 }}>
							<CaptionText
								sx={{ color: "text.secondary", display: "block", fontWeight: 600, letterSpacing: "0.08em", mb: 1 }}
							>
								{section.title.toUpperCase()}
							</CaptionText>
							<Divider sx={{ mb: 1 }} />
							{section.entries.map((entry) => (
								<Box
									key={entry.id}
									sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "space-between", py: 0.5 }}
								>
									<BodyTextSmall>{entry.label}</BodyTextSmall>
									<Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, gap: 0.75 }}>
										{entry.bindings.map((binding, index) => (
											<Box key={binding} sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
												{index > 0 && <CaptionText sx={{ color: "text.disabled" }}>or</CaptionText>}
												<Keycap binding={binding} isMac={isMac} />
											</Box>
										))}
									</Box>
								</Box>
							))}
						</Box>
					))
				)}
			</DialogContent>
		</Dialog>
	);
};

export default KeyboardShortcutsOverlay;
